import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, useAuth } from "../App";
import { Users, Loader2, Mail, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { isPresidentRole } from "../components/RoleScopeBanner";

const fmt = (iso) => (iso && iso.includes("-") ? iso.split("-").reverse().join("/") : iso || "—");

const MembersList = ({ members, tid }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={`${tid}-members`}>
    {(members || []).filter(m => m?.name).map((m, i) => (
      <Badge key={i} variant="outline"
             className={`text-xs gap-1 ${m.role === "president" ? "border-violet-500 bg-violet-50 text-violet-900" : m.role === "alternate" ? "border-slate-300 text-slate-500" : "border-slate-300 text-slate-800"}`}
             data-testid={`${tid}-member-${i}`}>
        <span className="font-medium">{m.name}</span>
        {m.grade && <span className="text-slate-500">· {m.grade}</span>}
        {m.role === "president" && <span>· Πρόεδρος</span>}
        {m.role === "alternate" && <span>· αναπλ.</span>}
        {m.email && <span className="inline-flex items-center gap-0.5 text-blue-700"><Mail className="w-3 h-3" />{m.email}</span>}
      </Badge>
    ))}
  </div>
);

const HistoryTable = ({ rows, tid }) => (
  rows.length === 0 ? (
    <p className="text-sm text-slate-500" data-testid={`${tid}-history-empty`}>Δεν έχει ανατεθεί ακόμη σε δικάσιμο.</p>
  ) : (
    <Table data-testid={`${tid}-history`}>
      <TableHeader>
        <TableRow className="bg-slate-50">
          <TableHead>Δικάσιμος</TableHead><TableHead>Πινάκιο</TableHead><TableHead className="text-right">Υποθ.</TableHead>
          <TableHead>Εισηγητές</TableHead><TableHead>Δικαστής Έδρας</TableHead><TableHead>Πράξεις</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={i} data-testid={`${tid}-history-row-${i}`}>
            <TableCell className="font-medium whitespace-nowrap">{fmt(r.hearing_date)}</TableCell>
            <TableCell><Link to="/hearings" className="text-primary underline">{r.pinakio_label}</Link></TableCell>
            <TableCell className="text-right">{r.cases}</TableCell>
            <TableCell className="text-xs">
              {r.rapporteurs.length ? r.rapporteurs.map(x => `${x.name} (${x.cases})`).join(", ") : <span className="text-amber-700">— χωρίς Εισηγητή</span>}
              {r.needs_reassignment > 0 && <Badge className="ml-1 bg-red-600 text-white text-[10px]">ΑΝΑΧΡΕΩΣΗ: {r.needs_reassignment}</Badge>}
            </TableCell>
            <TableCell className="text-xs">{r.bench_judges.join(", ") || "—"}</TableCell>
            <TableCell className="text-xs text-slate-600">{r.act_numbers.join(", ") || "—"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
);

export default function MyCompositions() {
  const { user } = useAuth();
  const president = isPresidentRole(user);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/me/compositions`).then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || "Σφάλμα φόρτωσης"));
  }, []);

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="my-compositions-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Users className="w-6 h-6 text-violet-700" />{president ? "Η Σύνθεσή Μου" : "Οι Συνθέσεις Μου"}</h1>
        <p className="text-slate-500 mt-1">{president ? "Μέλη της σύνθεσής σας και ιστορικό χρεώσεων ανά δικάσιμο." : "Συνθέσεις των Τομέων σας — μέλη, emails και ιστορικό χρεώσεων ανά δικάσιμο."}</p>
      </div>
      {error && <p className="text-sm text-red-600 flex items-center gap-1" data-testid="my-compositions-error"><AlertTriangle className="w-4 h-4" />{error}</p>}
      {!data && !error && <Loader2 className="w-6 h-6 animate-spin text-slate-400" />}
      {data && data.compositions.length === 0 && (
        <Card><CardContent className="p-6 text-sm text-amber-800" data-testid="my-compositions-empty">
          {president ? "Δεν έχει αντιστοιχιστεί Σύνθεση στον λογαριασμό σας — επικοινωνήστε με τον Διαχειριστή." : "Δεν έχουν οριστεί Συνθέσεις στους Τομείς σας (Προδικαστικό → Τομείς & Πράξεις)."}
        </CardContent></Card>
      )}
      {(data?.compositions || []).map((c, i) => {
        const tid = `my-composition-${i}`;
        return (
          <Card key={i} data-testid={tid}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Σύνθεση «{c.label}»</CardTitle>
              <CardDescription>{c.sector_number}ος Τομέας {data.court_type}{c.sector_title ? ` — ${c.sector_title}` : ""}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <MembersList members={c.members} tid={tid} />
              <HistoryTable rows={c.history || []} tid={tid} />
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
